import {
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEvent,
  useRef,
  FocusEvent,
  RefObject,
  useLayoutEffect,
} from "react";
import styles from "./Popover.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";
import { usePopoverContext } from "./internal/usePopoverContext";
import { useFocusWithin } from "../../internal/hooks";

/**
 * Props for the PopoverContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PopoverContentProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "popover"
> & {
  /**
   * Reference to the scrollable content area
   */
  scrollerRef?: RefObject<HTMLDivElement>;
  /**
   * Whether to remove the content from the DOM while the popover is closed
   */
  hideWhileClosed?: boolean;
};

/**
 * PopoverContent component for the main popover content area.
 *
 * Features:
 * - Automatic positioning and sizing based on trigger
 * - Screen boundary detection and fitting
 * - Scroll handling with custom scroller reference
 * - Focus management and keyboard navigation
 * - Hover interaction support for tooltip mode
 * - Escape key handling for closing
 * - Tab key trapping for modal popovers
 * - Arrow/caret indicator with automatic positioning
 * - Context integration with parent popover
 * - Accessibility support with proper ARIA roles
 *
 * @example
 * <Popover.Content>
 *   <p>This is the popover content</p>
 *   <Popover.Close>Close</Popover.Close>
 * </Popover.Content>
 *
 * @example
 * <Popover.Content scrollerRef={scrollRef}>
 *   <div style={{ height: '200px', overflow: 'auto' }}>
 *     {longContent}
 *   </div>
 * </Popover.Content>
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent(props, ref) {
    const {
      children,
      className,
      style,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onFocus,
      onBlur,
      onScroll,
      onScrollCapture,
      scrollerRef,
      hideWhileClosed = false,
      ...rest
    } = props;
    const context = usePopoverContext();
    const popoverClassNames = cx(className, styles["content"], {
      [styles["no-padding"]]: context?.noPadding,
    });
    const childrenRef = useRef<HTMLDivElement>(null);
    const combinedStyles = {
      ...style,
      ...context?.popover.style,
    };

    const { props: wrapperProps } = useFocusWithin({
      otherRelatedElements: context?.invoker.element
        ? [context?.invoker.element]
        : [],
      onBlur: (e) => {
        if (context?.modal) {
          e.preventDefault();
          requestAnimationFrame(() => {
            context?.invoker.element?.focus();
          });
          return;
        }
        onBlur?.(e as FocusEvent<HTMLDivElement>);
        context?.popover.close();
      },
      onFocus: (e) => {
        onFocus?.(e as FocusEvent<HTMLDivElement>);
        context?.popover.show();
      },
    });

    const onMouseEnterHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      if (!context?.openOnHover) return;
      if (!context?.open) return; // Don't set hover state if popover is closed
      context.setContentHovered(true);
    };
    const onMouseLeaveHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      if (!context?.openOnHover) return;
      context.setContentHovered(false);
    };

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
      if (e.code === "Escape" && !context?.disableCloseOnEscape) {
        context?.popover.close();
      }
    };

    const popoverContentRef = useMergeRefs([context?.popover.ref, ref]);
    const popoverScrollerRef = useMergeRefs([childrenRef, scrollerRef]);

    // Reset scroll position when popover opens
    useLayoutEffect(() => {
      if (context?.open && childrenRef.current) {
        childrenRef.current.scrollTop = 0;
        childrenRef.current.scrollLeft = 0;
      }
    }, [context?.open]);

    // Extract base side from actual placement for hover bridge positioning
    const bridgeSide =
      context?.openOnHover && context?.actualPlacement
        ? (context.actualPlacement.split("-")[0] as
            | "top"
            | "bottom"
            | "left"
            | "right")
        : undefined;

    return (
      <div
        popover={"manual"}
        id={context?.popover.id}
        data-anv="popover-content"
        data-position={bridgeSide}
        className={popoverClassNames}
        ref={popoverContentRef}
        style={combinedStyles}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        role="region"
        {...rest}
        onFocus={wrapperProps.onFocus}
        onBlur={wrapperProps.onBlur}
      >
        <div
          className={styles.scroller}
          role="presentation"
          tabIndex={-1}
          ref={popoverScrollerRef}
          onKeyDown={onKeyDownHandler}
          onScroll={onScroll}
          onScrollCapture={onScrollCapture}
        >
          {hideWhileClosed ? (context?.open ? children : null) : children}
        </div>
        {!context?.disableCaret && (
          <span className={styles["arrow"]} ref={context?.popover.arrowRef}>
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14">
              <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z"></path>
              <clipPath id=":r9:">
                <rect x="0" y="0" width="14" height="14"></rect>
              </clipPath>
            </svg>
          </span>
        )}
      </div>
    );
  },
);

PopoverContent.displayName = "PopoverContent";
